import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createInitialState, updateGame, getMatchMinuteDisplay, switchToNearestPlayer } from '../game/engine';
import { drawField, drawGoals, drawBall, drawPlayer, drawActionIndicators, drawRadar, drawPowerBar, drawPlayerStamina, drawSprintTrail } from '../game/renderer';
import { GAME_PHASE } from '../game/constants';

export default function Game({ role, homeTeam: homeTeamData, awayTeam: awayTeamData, twoPlayer, onBack, onMatchEnd }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const inputRef = useRef({
    joystick: { active: false, dx: 0, dy: 0 },
    shoot: false,
    pass: false,
    throughPass: false,
    diveLeft: false,
    diveRight: false,
    jumpUp: false,
    sprint: false,
    switchPlayer: false,
    chargingShoot: false,
    skillMove: false,
  });
  const animFrameRef = useRef(null);
  const [score, setScore] = useState({ home: 0, away: 0 });
  const [matchMinute, setMatchMinute] = useState(0);
  const [half, setHalf] = useState(1);
  const [message, setMessage] = useState('1. DEVRE');
  const [messageType, setMessageType] = useState('info');
  const [goalScorer, setGoalScorer] = useState(null);
  const [events, setEvents] = useState([]);
  const [phase, setPhase] = useState(GAME_PHASE.KICKOFF);
  const [isSprinting, setIsSprinting] = useState(false);
  const [possession, setPossession] = useState({ home: 50, away: 50 });
  const [shootPower, setShootPower] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [gamepadStatus, setGamepadStatus] = useState({ connected: false, label: 'Yok' });
  const [gamepad2Status, setGamepad2Status] = useState({ connected: false, label: 'Yok' });

  const joystickRef = useRef(null);
  const joystickKnobRef = useRef(null);
  const joystickTouchId = useRef(null);
  const joystickCenter = useRef({ x: 0, y: 0 });
  const gamepadDrivingRef = useRef(false);

  // 2. oyuncu input
  const input2Ref = useRef({
    joystick: { active: false, dx: 0, dy: 0 },
    shoot: false,
    pass: false,
    throughPass: false,
    diveLeft: false,
    diveRight: false,
    jumpUp: false,
    sprint: false,
    switchPlayer: false,
    chargingShoot: false,
    skillMove: false,
  });

  // Gamepad kontrolleri (PS/Xbox/Generic - standard mapping)
  useEffect(() => {
    let rafId = null;
    const prevButtons = {};

    const getButtonPressed = (gp, idx) => Boolean(gp.buttons[idx] && gp.buttons[idx].pressed);
    const getButtonValue = (gp, idx) => (gp.buttons[idx] ? gp.buttons[idx].value || 0 : 0);

    const justPressed = (gp, idx) => {
      const key = `${gp.index}:${idx}`;
      const isPressed = getButtonPressed(gp, idx);
      const wasPressed = Boolean(prevButtons[key]);
      prevButtons[key] = isPressed;
      return isPressed && !wasPressed;
    };

    function pollGamepad() {
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      const gp = pads ? Array.from(pads).find(Boolean) : null;
      const gp2 = twoPlayer && pads ? Array.from(pads).filter(Boolean)[1] || null : null;

      if (gp) {
        setGamepadStatus({ connected: true, label: gp.id || 'Bilinmeyen kumanda' });

        // Sol analog veya d-pad ile hareket
        const DEADZONE = 0.2;
        const lx = gp.axes[0] || 0;
        const ly = gp.axes[1] || 0;

        let moveX = lx;
        let moveY = ly;

        if (Math.abs(moveX) < DEADZONE) moveX = 0;
        if (Math.abs(moveY) < DEADZONE) moveY = 0;

        // Analog yoksa d-pad fallback
        if (moveX === 0 && moveY === 0) {
          if (getButtonPressed(gp, 12)) moveY -= 1; // up
          if (getButtonPressed(gp, 13)) moveY += 1; // down
          if (getButtonPressed(gp, 14)) moveX -= 1; // left
          if (getButtonPressed(gp, 15)) moveX += 1; // right
        }

        const len = Math.hypot(moveX, moveY);
        if (len > 0) {
          const intensity = Math.min(1, len);
          inputRef.current.joystick = {
            active: true,
            dx: (moveX / len) * intensity,
            dy: (moveY / len) * intensity,
          };
          gamepadDrivingRef.current = true;
        } else if (gamepadDrivingRef.current) {
          inputRef.current.joystick = { active: false, dx: 0, dy: 0 };
          gamepadDrivingRef.current = false;
        }

        if (role === 'player') {
          // X: şut (basılı tutma ile güç biriktirme)
          inputRef.current.chargingShoot = getButtonPressed(gp, 0);
          // O: pas
          if (justPressed(gp, 1)) inputRef.current.pass = true;
          // Kare: ara pas
          if (justPressed(gp, 2)) inputRef.current.throughPass = true;
          // Üçgen/L1: oyuncu değiştir
          if (justPressed(gp, 3) || justPressed(gp, 4)) inputRef.current.switchPlayer = true;
          // R3: skill move
          if (justPressed(gp, 11)) inputRef.current.skillMove = true;
        } else {
          // Kaleci
          if (justPressed(gp, 0)) inputRef.current.jumpUp = true;
          if (justPressed(gp, 1)) inputRef.current.diveRight = true;
          if (justPressed(gp, 2)) inputRef.current.diveLeft = true;
        }

        // Sprint: R1 veya R2 tetik
        inputRef.current.sprint = getButtonPressed(gp, 5) || getButtonValue(gp, 7) > 0.3;
      } else {
        setGamepadStatus({ connected: false, label: 'Yok' });
        if (gamepadDrivingRef.current) {
          inputRef.current.joystick = { active: false, dx: 0, dy: 0 };
          gamepadDrivingRef.current = false;
        }
      }

      // 2. oyuncu gamepad
      if (twoPlayer) {
        if (gp2) {
          setGamepad2Status({ connected: true, label: gp2.id || 'Bilinmeyen kumanda' });
          const DEADZONE2 = 0.2;
          let lx2 = gp2.axes[0] || 0;
          let ly2 = gp2.axes[1] || 0;
          if (Math.abs(lx2) < DEADZONE2) lx2 = 0;
          if (Math.abs(ly2) < DEADZONE2) ly2 = 0;
          if (lx2 === 0 && ly2 === 0) {
            if (getButtonPressed(gp2, 12)) ly2 -= 1;
            if (getButtonPressed(gp2, 13)) ly2 += 1;
            if (getButtonPressed(gp2, 14)) lx2 -= 1;
            if (getButtonPressed(gp2, 15)) lx2 += 1;
          }
          const len2 = Math.hypot(lx2, ly2);
          if (len2 > 0) {
            const intensity2 = Math.min(1, len2);
            input2Ref.current.joystick = {
              active: true,
              dx: (lx2 / len2) * intensity2,
              dy: (ly2 / len2) * intensity2
            };
          } else {
            input2Ref.current.joystick = { active: false, dx: 0, dy: 0 };
          }
          // X: şut
          input2Ref.current.chargingShoot = getButtonPressed(gp2, 0);
          // O: pas
          if (justPressed(gp2, 1)) input2Ref.current.pass = true;
          // Kare: ara pas
          if (justPressed(gp2, 2)) input2Ref.current.throughPass = true;
          // Üçgen/L1: oyuncu değiştir
          if (justPressed(gp2, 3) || justPressed(gp2, 4)) input2Ref.current.switchPlayer = true;
          // R3: skill move
          if (justPressed(gp2, 11)) input2Ref.current.skillMove = true;
          // Sprint
          input2Ref.current.sprint = getButtonPressed(gp2, 5) || getButtonValue(gp2, 7) > 0.3;
        } else {
          setGamepad2Status({ connected: false, label: 'Yok' });
          input2Ref.current.joystick = { active: false, dx: 0, dy: 0 };
        }
      }

      rafId = requestAnimationFrame(pollGamepad);
    }

    rafId = requestAnimationFrame(pollGamepad);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [role, twoPlayer]);

  // Fullscreen + Landscape kilidi
  useEffect(() => {
    // 5 saniye sonra kontrol ipuçlarını gizle
    const hideTimer = setTimeout(() => setShowControls(false), 5000);
    const requestFullscreen = () => {
      const el = document.documentElement;
      const rfs = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (rfs) {
        rfs.call(el).then(() => {
          // Landscape kilitle
          if (screen.orientation && screen.orientation.lock) {
            screen.orientation.lock('landscape').catch(() => {});
          }
        }).catch(() => {});
      }
    };
    // İlk dokunuşta fullscreen yap (mobil tarayıcılar kullanıcı etkileşimi ister)
    const onFirstTouch = () => {
      requestFullscreen();
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
    };
    window.addEventListener('touchstart', onFirstTouch, { once: true });
    window.addEventListener('click', onFirstTouch, { once: true });

    return () => {
      clearTimeout(hideTimer);
      window.removeEventListener('touchstart', onFirstTouch);
      window.removeEventListener('click', onFirstTouch);
      // Oyundan çıkınca fullscreen'den çık
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const containerRef = useRef(null);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    if (w === 0 || h === 0) return;
    canvas.width = w;
    canvas.height = h;
    if (stateRef.current) {
      stateRef.current.canvasW = w;
      stateRef.current.canvasH = h;
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    canvas.width = Math.round(rect.width) || window.innerWidth;
    canvas.height = Math.round(rect.height) || window.innerHeight;
    stateRef.current = createInitialState(canvas.width, canvas.height, role, homeTeamData, awayTeamData);
    if (twoPlayer) stateRef.current.twoPlayer = true;
    window.addEventListener('resize', resizeCanvas);
    const onOrientationChange = () => {
      setTimeout(resizeCanvas, 150);
      setTimeout(resizeCanvas, 500);
    };
    window.addEventListener('orientationchange', onOrientationChange);
    // İlk yüklemede bir kez daha kontrol et
    setTimeout(resizeCanvas, 100);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, [role, homeTeamData, awayTeamData, resizeCanvas]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    function gameLoop() {
      if (!stateRef.current) return;
      const state = updateGame(stateRef.current, inputRef.current, twoPlayer ? input2Ref.current : null);

      setScore({ home: state.homeScore, away: state.awayScore });
      setMatchMinute(getMatchMinuteDisplay(state));
      setHalf(state.half);
      setMessage(state.message);
      setMessageType(state.messageType);
      setGoalScorer(state.goalScorer);
      setEvents([...state.events]);
      setPhase(state.phase);
      setShootPower(state.shootPower);
      setStats(state.stats);
      
      // Top sahipliği hesapla
      if (state.possession.total > 0) {
        setPossession({
          home: Math.round((state.possession.home / state.possession.total) * 100) || 50,
          away: Math.round((state.possession.away / state.possession.total) * 100) || 50,
        });
      }

      // Devre arası veya maç sonu gelince stats göster
      if (state.phase === GAME_PHASE.HALF_TIME || state.phase === GAME_PHASE.FULL_TIME) {
        setShowStats(true);
      }

      render(ctx, state);
      animFrameRef.current = requestAnimationFrame(gameLoop);
    }

    animFrameRef.current = requestAnimationFrame(gameLoop);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, []);

  function render(ctx, state) {
    const { canvasW: w, canvasH: h, ball, homeTeam, awayTeam, controlledTeam, controlledPlayerIndex } = state;
    ctx.clearRect(0, 0, w, h);

    const controlledPlayer = (controlledTeam === 'home' ? homeTeam : awayTeam)[controlledPlayerIndex];
    const focusX = controlledPlayer ? (ball.x * 0.72 + controlledPlayer.x * 0.28) : ball.x;
    const focusY = controlledPlayer ? (ball.y * 0.72 + controlledPlayer.y * 0.28) : ball.y;
    const camOffsetX = Math.max(-52, Math.min(52, (w / 2 - focusX) * 0.18));
    const camOffsetY = Math.max(-34, Math.min(34, (h / 2 - focusY) * 0.18));
    const ballSpeed = Math.hypot(ball.vx, ball.vy) + Math.abs(ball.vz) * 0.45;
    const dynamicZoom = Math.max(0.78, Math.min(0.9, 0.78 + ballSpeed * 0.007));

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.scale(dynamicZoom, dynamicZoom);
    ctx.translate(-w / 2 + camOffsetX, -h / 2 + camOffsetY);
    if (state.cameraShake > 0) {
      const shakeMag = state.cameraShake * 0.55;
      const ox = (Math.random() - 0.5) * shakeMag;
      const oy = (Math.random() - 0.5) * shakeMag;
      ctx.translate(ox, oy);
    }

    drawField(ctx, w, h);
    drawGoals(ctx, w, h);

    // Sprint izi efekti
    if (state.isSprinting && controlledPlayer) {
      drawSprintTrail(ctx, controlledPlayer, true);
    }

    for (let i = 0; i < homeTeam.length; i++) {
      if (homeTeam[i].redCard) continue;
      drawPlayer(ctx, homeTeam[i], controlledTeam === 'home' && i === controlledPlayerIndex);
      drawPlayerStamina(ctx, homeTeam[i], controlledTeam === 'home' && i === controlledPlayerIndex);
    }
    for (let i = 0; i < awayTeam.length; i++) {
      if (awayTeam[i].redCard) continue;
      drawPlayer(ctx, awayTeam[i], controlledTeam === 'away' && i === controlledPlayerIndex);
      drawPlayerStamina(ctx, awayTeam[i], controlledTeam === 'away' && i === controlledPlayerIndex);
    }

    drawBall(ctx, ball);

    // Hamle yön okları
    drawActionIndicators(ctx, state);

    if (state.skillFxTimer > 0 && state.skillFx) {
      const t = state.skillFxTimer / 18;
      const radius = 14 + (1 - t) * 22;
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.2 + t * 0.6})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(state.skillFx.x, state.skillFx.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Güç barı (şut şarj ediyorken)
    if (state.isChargingShoot && controlledPlayer) {
      drawPowerBar(ctx, controlledPlayer.x - 30, controlledPlayer.y - 35, 60, 8, state.shootPower);
    }

    // FIFA tarzı radar mini-harita (sağ alt)
    const radarW = Math.min(160, w * 0.18);
    const radarH = Math.min(90, h * 0.2);
    drawRadar(ctx, state, w - radarW - 10, 45, radarW, radarH);

    // Gol kutlama efekti
    if (state.phase === GAME_PHASE.CELEBRATION) {
      drawCelebration(ctx, w, h, state.celebrationTimer);
    }

    ctx.restore();
  }

  function drawCelebration(ctx, w, h, timer) {
    // FIFA tarzı gol kutlaması
    // Kamera flaş efekti
    if (timer > 140) {
      ctx.fillStyle = `rgba(255,255,255,${(timer - 140) / 10 * 0.3})`;
      ctx.fillRect(0, 0, w, h);
    }

    // Konfeti efekti (daha yoğun)
    const count = 50;
    const colors = ['#f1c40f', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#fff', '#ff6b6b', '#ffd93d'];
    for (let i = 0; i < count; i++) {
      const seed = i * 137.508;
      const cx = (w * 0.1) + (seed * 7.3) % (w * 0.8);
      const cy = ((150 - timer) * 4 + seed * 2.1) % (h * 0.9);
      const size = 2 + (seed % 6);
      ctx.fillStyle = colors[i % colors.length];
      ctx.globalAlpha = 0.85;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(seed + timer * 0.15);
      // Farklı şekiller
      if (i % 3 === 0) {
        ctx.fillRect(-size / 2, -size / 2, size, size * 2);
      } else if (i % 3 === 1) {
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(size, size);
        ctx.lineTo(-size, size);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // Kenar parlaması
    const glowAlpha = Math.sin(timer * 0.1) * 0.15 + 0.1;
    ctx.strokeStyle = `rgba(241,196,15,${glowAlpha})`;
    ctx.lineWidth = 4;
    ctx.strokeRect(5, 5, w - 10, h - 10);
  }

  // Klavye kontrolleri
  useEffect(() => {
    const keys = new Set();

    function handleKeyDown(e) {
      keys.add(e.key.toLowerCase());
      updateKeyboardInput(keys);

      if (e.key === ' ' || e.key === 'Enter') {
        if (role === 'player') {
          inputRef.current.chargingShoot = true;
        }
      }
      if (e.key.toLowerCase() === 'a') {
        if (role === 'player') inputRef.current.pass = true;
        else inputRef.current.diveLeft = true;
      }
      if (e.key.toLowerCase() === 'q') {
        if (role === 'player') inputRef.current.throughPass = true;
      }
      if (e.key.toLowerCase() === 'd') {
        if (role === 'goalkeeper') inputRef.current.diveRight = true;
      }
      if (e.key.toLowerCase() === 'w') {
        if (role === 'goalkeeper') inputRef.current.jumpUp = true;
      }
      if (e.key === 'Shift') {
        inputRef.current.sprint = true;
      }
      if (e.key.toLowerCase() === 'e' || e.key.toLowerCase() === 's') {
        inputRef.current.switchPlayer = true;
      }
      if (e.key.toLowerCase() === 'r' && role === 'player') {
        inputRef.current.skillMove = true;
      }
      // 2. Oyuncu klavye (WASD + FGTR) - sadece twoPlayer modunda
      if (twoPlayer) {
        if (e.key.toLowerCase() === 'k') input2Ref.current.chargingShoot = true;
        if (e.key.toLowerCase() === 'j') input2Ref.current.pass = true;
        if (e.key.toLowerCase() === 'u') input2Ref.current.throughPass = true;
        if (e.key.toLowerCase() === 'l') input2Ref.current.switchPlayer = true;
        if (e.key.toLowerCase() === 'o') input2Ref.current.sprint = true;
        if (e.key.toLowerCase() === 'i') input2Ref.current.skillMove = true;
      }
    }

    function handleKeyUp(e) {
      keys.delete(e.key.toLowerCase());
      updateKeyboardInput(keys);
      if (e.key === 'Shift') {
        inputRef.current.sprint = false;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        inputRef.current.chargingShoot = false;
      }
      // 2. Oyuncu klavye
      if (twoPlayer) {
        if (e.key.toLowerCase() === 'k') input2Ref.current.chargingShoot = false;
        if (e.key.toLowerCase() === 'o') input2Ref.current.sprint = false;
      }
    }

    function updateKeyboardInput(keys) {
      let dx = 0, dy = 0;
      if (keys.has('arrowleft')) dx -= 1;
      if (keys.has('arrowright')) dx += 1;
      if (keys.has('arrowup')) dy -= 1;
      if (keys.has('arrowdown')) dy += 1;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0) {
        inputRef.current.joystick = { active: true, dx: dx / len, dy: dy / len };
      } else {
        inputRef.current.joystick = { active: false, dx: 0, dy: 0 };
      }
      // 2. oyuncu WASD
      if (twoPlayer) {
        let dx2 = 0, dy2 = 0;
        if (keys.has('a')) dx2 -= 1;
        if (keys.has('d')) dx2 += 1;
        if (keys.has('w')) dy2 -= 1;
        if (keys.has('s')) dy2 += 1;
        const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        if (len2 > 0) {
          input2Ref.current.joystick = { active: true, dx: dx2 / len2, dy: dy2 / len2 };
        } else {
          input2Ref.current.joystick = { active: false, dx: 0, dy: 0 };
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [role, twoPlayer]);

  // Joystick dokunmatik
  const handleJoystickStart = useCallback((e) => {
    e.preventDefault();
    const touch = e.changedTouches[0];
    joystickTouchId.current = touch.identifier;
    const rect = joystickRef.current.getBoundingClientRect();
    joystickCenter.current = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    updateJoystick(touch.clientX, touch.clientY);
  }, []);

  const handleJoystickMove = useCallback((e) => {
    e.preventDefault();
    for (const touch of e.changedTouches) {
      if (touch.identifier === joystickTouchId.current) {
        updateJoystick(touch.clientX, touch.clientY);
      }
    }
  }, []);

  const handleJoystickEnd = useCallback((e) => {
    for (const touch of e.changedTouches) {
      if (touch.identifier === joystickTouchId.current) {
        joystickTouchId.current = null;
        inputRef.current.joystick = { active: false, dx: 0, dy: 0 };
        if (joystickKnobRef.current) {
          joystickKnobRef.current.style.transform = 'translate(-50%, -50%)';
        }
      }
    }
  }, []);

  function updateJoystick(touchX, touchY) {
    const center = joystickCenter.current;
    let dx = touchX - center.x;
    let dy = touchY - center.y;
    const maxDist = 45;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > maxDist) {
      dx = (dx / dist) * maxDist;
      dy = (dy / dist) * maxDist;
    }

    if (joystickKnobRef.current) {
      joystickKnobRef.current.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    }

    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 5) {
      inputRef.current.joystick = { active: true, dx: dx / maxDist, dy: dy / maxDist };
    } else {
      inputRef.current.joystick = { active: false, dx: 0, dy: 0 };
    }
  }

  // Aksiyon butonları
  const handleAction = useCallback((action) => {
    inputRef.current[action] = true;
  }, []);

  return (
    <div className="game-container" ref={containerRef}>
      <canvas ref={canvasRef} className="game-canvas" />

      {/* FIFA-Style HUD */}
      <div className="hud">
        <div className="scoreboard fifa-board">
          <div className="sb-team home-side">
            <div className="sb-badge" style={{
              background: homeTeamData.colors.primary,
              color: homeTeamData.colors.secondary,
              border: `2px solid ${homeTeamData.colors.secondary}`
            }}>
              {homeTeamData.shortName}
            </div>
            <span className="sb-team-name">{homeTeamData.name}</span>
          </div>
          <div className="sb-score-center">
            <span className="score">{score.home}</span>
            <div className="sb-time-box">
              <span className="sb-minute">{matchMinute}'</span>
              <span className="sb-half">{half === 1 ? '1D' : '2D'}</span>
            </div>
            <span className="score">{score.away}</span>
          </div>
          <div className="sb-team away-side">
            <span className="sb-team-name">{awayTeamData.name}</span>
            <div className="sb-badge" style={{
              background: awayTeamData.colors.primary,
              color: awayTeamData.colors.secondary,
              border: `2px solid ${awayTeamData.colors.secondary}`
            }}>
              {awayTeamData.shortName}
            </div>
          </div>
        </div>
        {/* Possession Bar */}
        <div className="possession-bar">
          <span className="poss-pct home-poss">{possession.home}%</span>
          <div className="poss-track">
            <div className="poss-fill home-fill" style={{width: `${possession.home}%`}} />
          </div>
          <span className="poss-pct away-poss">{possession.away}%</span>
        </div>
      </div>

      <button className="back-btn ps-back-btn" onClick={onBack}>
        <span className="ps-hint-btn small">○</span> Menü
      </button>

      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 30,
          color: '#fff',
          background: gamepadStatus.connected ? 'rgba(46, 204, 113, 0.75)' : 'rgba(231, 76, 60, 0.75)',
          border: '1px solid rgba(255,255,255,0.35)',
          borderRadius: 8,
          padding: '6px 10px',
          fontSize: 12,
          maxWidth: '45vw',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
        title={gamepadStatus.label}
      >
        {gamepadStatus.connected ? 'Gamepad bagli' : 'Gamepad bagli degil'}
      </div>

      {twoPlayer && (
        <div
          style={{
            position: 'absolute',
            top: 40,
            right: 8,
            zIndex: 30,
            color: '#fff',
            background: gamepad2Status.connected ? 'rgba(46, 204, 113, 0.75)' : 'rgba(231, 76, 60, 0.75)',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 8,
            padding: '6px 10px',
            fontSize: 12,
            maxWidth: '45vw',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          title={gamepad2Status.label}
        >
          {gamepad2Status.connected ? '2P Gamepad bagli' : '2P Gamepad bagli degil'}
        </div>
      )}

      {/* Gol olayları */}
      {events.length > 0 && (
        <div className="events-bar">
          {events.map((ev, i) => (
            <div key={i} className={`event-item ${ev.team} ${ev.type}`}>
              {ev.type === 'goal' && `⚽ ${ev.minute}' ${ev.player}`}
              {ev.type === 'yellow_card' && `🟨 ${ev.minute}' ${ev.player}`}
              {ev.type === 'red_card' && `🟥 ${ev.minute}' ${ev.player}`}
            </div>
          ))}
        </div>
      )}

      {/* Mesaj overlay */}
      {message && (
        <div className="message-overlay">
          <div className={`message-text ${messageType === 'goal' ? 'goal' : ''} ${messageType === 'save' ? 'save' : ''} ${messageType === 'halftime' ? 'halftime' : ''} ${messageType === 'fulltime' ? 'fulltime' : ''} ${messageType === 'card' ? 'card-msg' : ''} ${messageType === 'foul' ? 'foul-msg' : ''}`}>
            {message}
            {messageType === 'goal' && goalScorer && (
              <div className="goal-scorer">⚽ {goalScorer.name} {matchMinute}'</div>
            )}
            {(messageType === 'halftime' || messageType === 'fulltime') && stats && (
              <div className="match-stats-overlay">
                <div className="stats-row">
                  <span>{stats.home.shots}</span>
                  <span className="stat-label">Şut</span>
                  <span>{stats.away.shots}</span>
                </div>
                <div className="stats-row">
                  <span>{stats.home.shotsOnTarget}</span>
                  <span className="stat-label">İsabetli Şut</span>
                  <span>{stats.away.shotsOnTarget}</span>
                </div>
                <div className="stats-row">
                  <span>{possession.home}%</span>
                  <span className="stat-label">Top Sahipliği</span>
                  <span>{possession.away}%</span>
                </div>
                <div className="stats-row">
                  <span>{stats.home.fouls}</span>
                  <span className="stat-label">Faul</span>
                  <span>{stats.away.fouls}</span>
                </div>
                <div className="stats-row">
                  <span>{stats.home.corners}</span>
                  <span className="stat-label">Korner</span>
                  <span>{stats.away.corners}</span>
                </div>
                <div className="stats-row">
                  <span>{stats.home.passes}</span>
                  <span className="stat-label">Pas</span>
                  <span>{stats.away.passes}</span>
                </div>
              </div>
            )}
            {messageType === 'fulltime' && (
              <div className="final-score">{score.home} - {score.away}</div>
            )}
            {messageType === 'fulltime' && onMatchEnd && (
              <button className="ts-start" style={{marginTop: 15, fontSize: '0.9rem'}}
                onClick={() => onMatchEnd(score.home, score.away)}>
                DEVAM ET
              </button>
            )}
            {messageType === 'fulltime' && !onMatchEnd && (
              <button className="ts-start" style={{marginTop: 15, fontSize: '0.9rem'}}
                onClick={onBack}>
                GERİ DÖN
              </button>
            )}
          </div>
        </div>
      )}

      {/* Joystick (sol - PS L3 stick) */}
      <div
        className="joystick-area"
        ref={joystickRef}
        onTouchStart={handleJoystickStart}
        onTouchMove={handleJoystickMove}
        onTouchEnd={handleJoystickEnd}
      >
        <div className="joystick-base ps-stick">
          <div className="joystick-knob ps-knob" ref={joystickKnobRef} />
        </div>
      </div>

      {/* PS Kontrol butonları - PlayStation düzeni (△○✕□) */}
      {role === 'player' ? (
        <>
          <div className="action-buttons ps-action-layout">
            {/* Sol: □ - Ara Pas */}
            <button
              className="ctrl-btn ps-btn ps-square"
              onTouchStart={(e) => { e.preventDefault(); handleAction('throughPass'); }}
              onMouseDown={() => handleAction('throughPass')}
            >
              <span className="ctrl-icon ps-symbol">□</span>
              <span className="ps-label">Ara Pas</span>
            </button>
            {/* Üst: △ - Şut (basılı tut) */}
            <button
              className="ctrl-btn ps-btn ps-triangle shoot-btn"
              onTouchStart={(e) => { e.preventDefault(); inputRef.current.chargingShoot = true; }}
              onTouchEnd={(e) => { e.preventDefault(); inputRef.current.chargingShoot = false; }}
              onMouseDown={() => { inputRef.current.chargingShoot = true; }}
              onMouseUp={() => { inputRef.current.chargingShoot = false; }}
            >
              <span className="ctrl-icon ps-symbol">△</span>
              <span className="ps-label">Şut</span>
              {shootPower > 0 && <div className="shoot-power-indicator" style={{width: `${shootPower * 100}%`}} />}
            </button>
            {/* Sağ: ○ - Pas */}
            <button
              className="ctrl-btn ps-btn ps-circle"
              onTouchStart={(e) => { e.preventDefault(); handleAction('pass'); }}
              onMouseDown={() => handleAction('pass')}
            >
              <span className="ctrl-icon ps-symbol">○</span>
              <span className="ps-label">Pas</span>
            </button>
          </div>
          {/* Alt: ✕ - Sprint (ayrı pozisyonda) */}
          <div className="ps-cross-sprint">
            <button
              className="ctrl-btn ps-btn ps-cross"
              onTouchStart={(e) => { e.preventDefault(); inputRef.current.sprint = true; setIsSprinting(true); }}
              onTouchEnd={(e) => { e.preventDefault(); inputRef.current.sprint = false; setIsSprinting(false); }}
              onMouseDown={() => { inputRef.current.sprint = true; setIsSprinting(true); }}
              onMouseUp={() => { inputRef.current.sprint = false; setIsSprinting(false); }}
            >
              <span className="ctrl-icon ps-symbol">✕</span>
              <span className="ps-label">Sprint</span>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="action-buttons ps-action-layout">
            <button
              className="ctrl-btn ps-btn ps-square"
              onTouchStart={(e) => { e.preventDefault(); handleAction('diveLeft'); }}
              onMouseDown={() => handleAction('diveLeft')}
            >
              <span className="ctrl-icon ps-symbol">□</span>
              <span className="ps-label">Sol At</span>
            </button>
            <button
              className="ctrl-btn ps-btn ps-triangle"
              onTouchStart={(e) => { e.preventDefault(); handleAction('jumpUp'); }}
              onMouseDown={() => handleAction('jumpUp')}
            >
              <span className="ctrl-icon ps-symbol">△</span>
              <span className="ps-label">Zıpla</span>
            </button>
            <button
              className="ctrl-btn ps-btn ps-circle"
              onTouchStart={(e) => { e.preventDefault(); handleAction('diveRight'); }}
              onMouseDown={() => handleAction('diveRight')}
            >
              <span className="ctrl-icon ps-symbol">○</span>
              <span className="ps-label">Sağ At</span>
            </button>
          </div>
        </>
      )}

      {/* PS R1/L1 butonları */}
      <div className="ps-shoulder-btns">
        <button
          className="ps-shoulder-btn ps-l1"
          onTouchStart={(e) => { e.preventDefault(); handleAction('skillMove'); }}
          onMouseDown={() => handleAction('skillMove')}
        >R1 Skill</button>
        <button
          className={`ps-shoulder-btn ps-r2 ${isSprinting ? 'active' : ''}`}
          onTouchStart={(e) => { e.preventDefault(); inputRef.current.sprint = true; setIsSprinting(true); }}
          onTouchEnd={(e) => { e.preventDefault(); inputRef.current.sprint = false; setIsSprinting(false); }}
          onMouseDown={() => { inputRef.current.sprint = true; setIsSprinting(true); }}
          onMouseUp={() => { inputRef.current.sprint = false; setIsSprinting(false); }}
        >R2 Sprint</button>
        <button
          className="ps-shoulder-btn ps-l1"
          onTouchStart={(e) => { e.preventDefault(); handleAction('switchPlayer'); }}
          onMouseDown={() => handleAction('switchPlayer')}
        >L1 Değiştir</button>
      </div>

      {/* PS Klavye kontrol ipuçları */}
      {showControls && (
        <div className="ps-keyboard-hints" onClick={() => setShowControls(false)}>
          <div className="ps-hints-box">
            <div className="ps-hints-title">⌨️ Klavye Kontrolleri</div>
            <div className="ps-hints-grid">
              <div className="ps-hint-row"><span className="ps-key">↑↓←→</span> Hareket</div>
              <div className="ps-hint-row"><span className="ps-key">SPACE</span> <span style={{color:'#00d4aa'}}>△</span> Şut (basılı tut)</div>
              <div className="ps-hint-row"><span className="ps-key">A</span> <span style={{color:'#ff4455'}}>○</span> Pas</div>
              <div className="ps-hint-row"><span className="ps-key">Q</span> <span style={{color:'#ee77aa'}}>□</span> Ara Pas</div>
              <div className="ps-hint-row"><span className="ps-key">SHIFT</span> R2 Sprint</div>
              <div className="ps-hint-row"><span className="ps-key">E/S</span> L1 Değiştir</div>
            </div>
            <div className="ps-hints-dismiss">Devam etmek için tıkla</div>
          </div>
        </div>
      )}
    </div>
  );
}
